import React, { useState } from 'react';
import { View, Text, TextInput, Alert, StyleSheet, useWindowDimensions, Platform, ScrollView, KeyboardAvoidingView } from 'react-native';
import theme from '../../theme';
import { getBackendUrl } from '../../lib/backendUrl';
import { fetchWithRetry } from '../../lib/fetchWithRetry';
import AppButton from '../../components/AppButton';
import { useI18n } from '../../i18n/useI18n';
import storage from '../../lib/storage';

async function buildDeviceHeaders() {
  const deviceIdKey = 'kakeibo_device_id';
  let deviceId = await storage.getItem(deviceIdKey);
  if (!deviceId) {
    deviceId = `inst-${Date.now()}-${Math.random().toString(36).slice(2,9)}`;
    try { await storage.setItem(deviceIdKey, deviceId); } catch (_) {}
  }
  const platform = (typeof navigator !== 'undefined' && (navigator.product || 'web')) as string;
  return {
    'X-Device-Id': deviceId,
    'X-App-Id': 'kakeibo',
    'X-Platform': platform,
  } as Record<string,string>;
}

type Props = {
  onClose: () => void;
  defaultBackend?: string;
  fullScreen?: boolean;
};

export default function ChangePasswordForm({ onClose, defaultBackend, fullScreen = false }: Props) {
  const { t } = useI18n();
  const { width } = useWindowDimensions();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  function validate() {
    if (!currentPassword) { setServerError(t('enterCurrentPassword')); return false; }
    if (!newPassword || newPassword.length < 4) { setServerError(t('passwordMin4')); return false; }
    if (newPassword !== confirmPassword) { setServerError(t('passwordsMismatch')); return false; }
    return true;
  }

  async function handleChange() {
    setServerError(null);
    if (!validate()) return;
    try {
      setLoading(true);
      const backend = getBackendUrl(defaultBackend);
      const extra = await buildDeviceHeaders();
      const res = await fetchWithRetry(`${backend}/auth/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...extra },
        body: JSON.stringify({ currentPassword, newPassword }),
      }, { retries: 2 });
      if (!res.ok) {
        let msg = t('changePasswordFailed');
        try {
          const body = await res.json().catch(() => null);
          if (body && body.error) {
            if (res.status === 401 || String(body.error).toLowerCase().includes('current')) msg = t('incorrectCurrentPassword');
            else msg = String(body.error || body.message || msg);
          } else {
            const txt = await res.text().catch(() => '');
            if (txt) msg = txt;
          }
        } catch (_) {}
        setServerError(msg);
        showAlert(t('error'), msg);
        return;
      }
      showAlert(t('changePassword'), t('passwordChanged'));
      onClose();
    } catch (e: any) {
      const msg = (e && e.message) ? e.message : t('changePasswordFailed');
      setServerError(msg);
      showAlert(t('error'), msg);
    } finally { setLoading(false); }
  }

  function showAlert(title?: string | null, message?: string | null) {
    try {
      if (Alert && typeof (Alert as any).alert === 'function') {
        (Alert as any).alert(title || '', message || '');
        return;
      }
    } catch (_) {}
    // Fallback for web or environments without Alert.alert
    try {
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert((title ? title + '\n' : '') + (message || ''));
        return;
      }
    } catch (_) {}
    // last resort
    console.warn('Alert:', title, message);
  }

  const isWide = width >= 700;
  const stackButtons = width < 380;
  const cardStyle = [
    styles.card,
    { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
  ] as Array<any>;

  if (fullScreen) {
    // Fullscreen flow should occupy available height; otherwise inner flex layouts can collapse to 0.
    cardStyle.push({ flex: 1, alignSelf: 'stretch', width: '100%', maxWidth: undefined, borderRadius: 0, borderWidth: 0 } as any);
  }
  // when in fullScreen mode on narrow screens, let card take full width
  if (fullScreen && !isWide) {
    cardStyle.push({ paddingHorizontal: 16, paddingTop: 16 } as any);
  }

  const body = (
    <>
      <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('currentPassword')}</Text>
      <TextInput value={currentPassword} onChangeText={setCurrentPassword} secureTextEntry style={[styles.input, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: theme.colors.border }]} />
      <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('newPassword')}</Text>
      <TextInput value={newPassword} onChangeText={setNewPassword} secureTextEntry style={[styles.input, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: theme.colors.border }]} />
      <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('confirmPassword')}</Text>
      <TextInput value={confirmPassword} onChangeText={setConfirmPassword} secureTextEntry style={[styles.input, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: theme.colors.border }]} />
      {serverError ? <Text style={styles.error}>{serverError}</Text> : null}
      <View style={{ height: 12 }} />
      <View style={[styles.actions, stackButtons ? styles.actionsStacked : null]}>
        <AppButton
          title={t('cancel')}
          onPress={onClose}
          tone="secondary"
          variant="outline"
          style={[styles.actionButton, stackButtons ? styles.actionButtonStacked : null]}
          textStyle={styles.actionButtonText}
        />

        <View style={stackButtons ? { height: 12 } : { width: 12 }} />
        <AppButton
          title={loading ? t('pleaseWait') : t('changePassword')}
          onPress={handleChange}
          tone="primary"
          style={[styles.actionButton, stackButtons ? styles.actionButtonStacked : null]}
          textStyle={styles.actionButtonText}
          disabled={loading}
        />
      </View>
    </>
  );

  return (
    <View style={[...cardStyle]}> 
      {fullScreen ? (
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={{ paddingBottom: 24 }}>
            {body}
          </ScrollView>
        </KeyboardAvoidingView>
      ) : (
        body
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: { width: '100%', maxWidth: 420, padding: 16, borderRadius: 8, borderWidth: 1 },
  label: { marginTop: 8, marginBottom: 4, fontWeight: '600' },
  input: { height: 40, borderWidth: 1, paddingHorizontal: 8, borderRadius: 4 },
  error: { color: theme.colors.error, marginTop: 6 },
  actions: { flexDirection: 'row', alignItems: 'stretch' },
  actionsStacked: { flexDirection: 'column' },
  actionButton: { flex: 1, minWidth: 0 },
  actionButtonStacked: { flex: undefined, width: '100%', alignSelf: 'stretch' },
  actionButtonText: { width: '100%', textAlign: 'center' },
});
